import { modelState, ModelStateList } from "@/utils/modelState";
import { RatesBaseRateOverrides } from "./CustomBaseRate";
import { RatesRateDiscountOverrides } from "./CustomRateDiscount";

export namespace RatesRatePlanOverrides {
    export const RatePlanOverride = Symbol("RatePlanOverride");
    export interface RatePlanOverride {
        [RatePlanOverride]: boolean;
    }

    export interface RatePlanOverrideDto {
        $type: string;
        value: RatePlanOverrideValueDto;
    }

    export interface RatePlanOverrideValueDto {
        //
    }

    export function serializeRatePlanOverride(action: RatePlanOverride): RatePlanOverrideDto {
        if (!action) {
            return null;
        }

        for (let ratePlanOverride of ratePlanOverrides) {
            if (ratePlanOverride.isInstanceOf(action)) {
                return {
                    $type: ratePlanOverride.key,
                    value: ratePlanOverride.serialize(action)
                };
            }
        }

        throw new Error("Invalid status type");
    }

    export function deserializeRatePlanOverride(dto: RatePlanOverrideDto): RatePlanOverride {
        if (!dto) {
            return null;
        }

        for (let ratePlanOverride of ratePlanOverrides) {
            if (ratePlanOverride.key === dto.$type) {
                return ratePlanOverride.deserialize(dto.value);
            }
        }

        throw new Error("Invalid status type");
    }

    export function getRatePlanOverrideName(status: RatePlanOverride): string {

        for (let ratePlanOverride of ratePlanOverrides) {
            if (ratePlanOverride.isInstanceOf(status)) {
                return ratePlanOverride.name;
            }
        }

        throw new Error("Invalid status type");
    }

    export function createRatePlanOverride(key: string): RatePlanOverride {

        for (let ratePlanOverride of ratePlanOverrides) {
            if (ratePlanOverride.key === key) {
                return ratePlanOverride.create();
            }
        }

        throw new Error("Invalid ratePlanOverride key");
    }



    export const ExistingRatePlanOverride = Symbol("ExistingRatePlanOverride");
    export interface ExistingRatePlanOverride extends RatePlanOverride {
        [ExistingRatePlanOverride]: boolean;
        RatePlanId: string;
        RatePlanVersion: number;
    }

    export function isExistingRatePlanOverride(val: RatePlanOverride): val is ExistingRatePlanOverride {
        return val && (val as ExistingRatePlanOverride)[ExistingRatePlanOverride] === true;
    }

    export interface ExistingRatePlanOverrideValueDto extends RatePlanOverrideValueDto {
        ratePlanId: string;
        ratePlanVersion: number;
    }

    export const ExistingRatePlanOverrideDefinition: RatePlanOverrideDefinitionT<ExistingRatePlanOverride, ExistingRatePlanOverrideValueDto> =
    {
        key: "Existing",
        name: "Existing",
        isInstanceOf: (ratePlanOverride: RatePlanOverride) => isExistingRatePlanOverride(ratePlanOverride),
        create: (): ExistingRatePlanOverride => {
            return {
                [RatePlanOverride]: true,
                [ExistingRatePlanOverride]: true,
                RatePlanId: null,
                RatePlanVersion: null
            };
        },
        serialize: (ratePlanOverride: ExistingRatePlanOverride): ExistingRatePlanOverrideValueDto => {
            return {
                ratePlanId: ratePlanOverride.RatePlanId,
                ratePlanVersion: ratePlanOverride.RatePlanVersion
            };
        },
        deserialize: function (this: RatePlanOverrideDefinition, dto: ExistingRatePlanOverrideValueDto | null) {
            if (!dto) {
                return null;
            }

            const existing = this.create() as ExistingRatePlanOverride;
            existing.RatePlanId = dto.ratePlanId;
            existing.RatePlanVersion = dto.ratePlanVersion;
            return existing;
        }
    };


    export const CustomRatePlanOverride = Symbol("CustomRatePlanOverride");
    export interface CustomRatePlanOverride extends RatePlanOverride {
        [CustomRatePlanOverride]: boolean;
        BaseRate: modelState<RatesBaseRateOverrides.BaseRateOverride | null>;
        RateDiscounts: ModelStateList<RatesRateDiscountOverrides.RateDiscountOverride>;
    }

    export function isCustomRatePlanOverride(val: RatePlanOverride): val is CustomRatePlanOverride {
        return val && (val as CustomRatePlanOverride)[CustomRatePlanOverride] === true;
    }

    export interface CustomRatePlanOverrideValueDto extends RatePlanOverrideValueDto {
        baseRate: RatesBaseRateOverrides.BaseRateOverrideDto;
        rateDiscounts: RatesRateDiscountOverrides.RateDiscountOverrideDto[];
    }

    export const CustomRatePlanOverrideDefinition: RatePlanOverrideDefinition = {
        key: "Custom",
        name: "Custom",
        isInstanceOf: (ratePlanOverride: RatePlanOverride) => isCustomRatePlanOverride(ratePlanOverride),
        create: (): CustomRatePlanOverride => {
            return {
                [RatePlanOverride]: true,
                [CustomRatePlanOverride]: true,
                BaseRate: modelState(null),
                RateDiscounts: new ModelStateList([])
            };
        },
        serialize: (ratePlanOverride: CustomRatePlanOverride): CustomRatePlanOverrideValueDto => {
            return {
                baseRate: RatesBaseRateOverrides.serializeBaseRateOverride(ratePlanOverride.BaseRate.value),
                rateDiscounts: ratePlanOverride.RateDiscounts.toArray().map(RatesRateDiscountOverrides.serializeRateDiscountOverride)
            };
        },
        deserialize: function (this: RatePlanOverrideDefinition, dto: CustomRatePlanOverrideValueDto | null) {
            if (!dto) {
                return null;
            }

            const custom = this.create() as CustomRatePlanOverride;
            custom.BaseRate.setValue(RatesBaseRateOverrides.deserializeBaseRateOverride(dto.baseRate));
            custom.BaseRate.commit();

            dto.rateDiscounts.map(x => custom.RateDiscounts.add(RatesRateDiscountOverrides.deserializeRateDiscountOverride(x)));
            custom.RateDiscounts.commitChange();

            return custom;
        }
    };


    interface RatePlanOverrideDefinition {
        key: string;
        name: string;
        isInstanceOf: (ratePlanOverride: RatePlanOverride) => boolean;
        create: () => RatePlanOverride;
        serialize: (ratePlanOverride: RatePlanOverride) => RatePlanOverrideValueDto;
        deserialize: (dto: RatePlanOverrideValueDto) => RatePlanOverride;
    }

    interface RatePlanOverrideDefinitionT<T, TDto> extends Omit<RatePlanOverrideDefinition, 'create' | 'serialize' | 'deserialize'> {
        create: () => T;
        serialize: (ratePlanOverride: T) => TDto;
        deserialize: (dto: TDto) => T;
    }

    const ratePlanOverrides: RatePlanOverrideDefinition[] = [
        ExistingRatePlanOverrideDefinition,
        CustomRatePlanOverrideDefinition
    ]
}
